import { Button, Popconfirm } from 'antd';
import React, { useEffect, useState } from 'react';
import { Edit, Trash2 } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../components/buttons/LoadingSpinner';
import SearchTable from '../../components/inputs/search/SearchTable';
import Table from '../../components/tables/Table';
import TableItem from '../../components/tables/TableItem';
import AssetCategory from '../../components/tags/AssetCategory';
import Checkmark from '../../components/tags/Checkmark';
import Tag from '../../components/tags/Tag';
import DateFormatted from '../../components/typography/Date';
import { AssetsService } from '../../services/assets/Assets';
import { setRootAside } from '../../state/component/AsideSlice';
import {
  deleteHardware,
  fetchHardware,
} from '../../state/hardware/HardwareSlice';
import { RootState } from '../../state/store';
import { addToast } from '../../state/toast/ToastSlice';
import {
  CompanyModules,
  hasModule,
} from '../../utils/auth/AuthorizationUtility';
import { Capitalize } from '../../utils/text/Capitalize';

export const HardwareOverview = () => {
  const { t } = useTranslation();
  const { hardware, loading } = useSelector(
    (state: RootState) => state.hardware
  );
  const modules = useSelector((state: RootState) => state.auth.modules);

  const [unassigned, setUnassigned] = useState(false);
  const [search, setSearch] = useState('');

  const [confirmLoading, setConfirmLoading] = useState(false);
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const service = new AssetsService();
  const dispatch = useDispatch();

  useEffect(() => {
    setUnassigned(false);
    if (companyId) {
      dispatch(fetchHardware(companyId));
    }
  }, [companyId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const filterAssets = (searchValue: string, unassigned: boolean) => {
    let filtered = hardware;

    if (searchValue.trim() !== '') {
      const lowerSearchValue = searchValue.toLowerCase();

      filtered = filtered.filter((asset) => {
        const invoiceNumber = asset?.ordPro_invoicenr;
        const username = asset?.ordSer_username;
        const productName = asset?.ordPro_naam;
        const serialNumber = asset?.ordSer_serial;
        const tags = asset?.tags?.map((tag) => tag.tag).join(', ') || '';

        return (
          String(invoiceNumber).toLowerCase().includes(lowerSearchValue) ||
          (username && username.toLowerCase().includes(lowerSearchValue)) ||
          (productName &&
            productName.toLowerCase().includes(lowerSearchValue)) ||
          (serialNumber &&
            serialNumber.toLowerCase().includes(lowerSearchValue)) ||
          tags.toLowerCase().includes(lowerSearchValue)
        );
      });
    }

    if (unassigned) {
      filtered = filtered.filter((asset) => asset.ordSer_user_id === null);
    }

    return filtered;
  };

  const filterByUserId = () => {
    setUnassigned((prevUnassigned) => {
      const updatedUnassigned = !prevUnassigned;
      filterAssets(search, updatedUnassigned);
      return updatedUnassigned;
    });
  };

  const viewDetail = (asset: string[]) => {
    dispatch(
      setRootAside({
        component: 'assetDetail',
        data: { asset: asset },
        title: 'Hardware',
      })
    );
  };

  const onAddHardware = () => {
    dispatch(
      setRootAside({
        component: 'addHardware',
        data: { hardware: hardware },
        title: 'Hardware toevoegen',
      })
    );
  };

  const onDelete = async (assetId: number) => {
    setConfirmLoading(true);
    try {
      await service.deleteAsset(companyId, assetId);
      dispatch(deleteHardware(assetId));
      dispatch(
        addToast({
          description: 'De hardware is verwijderd.',
          position: 'bottomRight',
          style: 'success',
        })
      );
    } catch (error) {
      console.error('Error deleting hardware:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const tableHeaders: object = [
    'Factuurnummer',
    'Factuurdatum',
    'Gebruiker',
    'Product',
    'Category',
    'Serienummer',
    'Tags',
    'Remote managed',
    '',
  ];

  const fixedColumns = [
    { key: 'Factuurnummer', position: 'left' },
    { key: '', position: 'right' },
  ];

  const filteredAssets = filterAssets(search, unassigned) || [];

  return hasModule(modules, CompanyModules.HARDWARE) ? (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-y-hidden border dark:border-gray-700 rounded-lg">
          <div className="px-6 py-2 border-b dark:border-gray-700 flex justify-between items-center">
            {t('Gevonden')}: {filteredAssets.length}
            <div className="flex gap-3 items-center">
              <Button
                onClick={filterByUserId}
                className={`${unassigned ? 'bg-blue-700 text-white' : ''}`}
              >
                {t('Niet toegewezen hardware')}
              </Button>
              <Button
                onClick={onAddHardware}
                className="bg-blue-700 text-white"
              >
                {t('+ Hardware')}
              </Button>
            </div>
          </div>
          <SearchTable
            handleChange={handleChange}
            placeholder="Hardware zoeken..."
            value={search}
          />
          <Table header={tableHeaders} fixedColumns={fixedColumns}>
            {filteredAssets?.length > 0 ? (
              filteredAssets?.map((asset: any) => (
                <tr
                  key={asset.ordSer_id}
                  onClick={() => viewDetail(asset)}
                  className="cursor-pointer"
                >
                  <TableItem type="stickyLeft">
                    {asset.ordPro_invoicenr ?? '/'}
                  </TableItem>
                  <TableItem>
                    {asset.ordPro_invoiceDate && (
                      <DateFormatted date={asset.ordPro_invoiceDate} />
                    )}
                  </TableItem>
                  <TableItem>
                    {Capitalize(asset.ordSer_username ?? '/')}
                  </TableItem>
                  <TableItem>{asset.ordPro_naam}</TableItem>
                  <TableItem>
                    <AssetCategory category={asset.ordPro_categorie} />
                  </TableItem>
                  <TableItem>{asset.ordSer_serial}</TableItem>

                  <TableItem>
                    {asset.tags?.length > 0 && (
                      <div className="flex gap-1">
                        {asset.tags
                          ?.slice(0, 2)
                          .map((tag, index) => (
                            <Tag
                              key={index}
                              style={'muted'}
                              label={tag.tag}
                              lowercase
                            />
                          ))}
                        {asset.tags?.length > 2 && (
                          <Tag
                            key="more"
                            style={'blue'}
                            label={`+${asset.tags?.length - 2}`}
                          />
                        )}
                      </div>
                    )}
                  </TableItem>
                  <TableItem>
                    <Checkmark check={asset.device_id ? 1 : 0} />
                  </TableItem>
                  <TableItem type="stickyRight">
                    <button onClick={() => viewDetail(asset)}>
                      <Edit size={18} />
                    </button>
                    <Popconfirm
                      title={t('Verwijder hardware')}
                      description={t('Wilt u deze hardware verwijderen?')}
                      onConfirm={(e) => {
                        e.stopPropagation();
                        onDelete(asset.ordSer_id);
                      }}
                      onCancel={(e) => {
                        e.stopPropagation();
                      }}
                      okText={t('Verwijder')}
                      cancelText={t('Annuleer')}
                      okButtonProps={{ loading: confirmLoading }}
                      okType="danger"
                      icon={null}
                    >
                      <button
                        className="text-red-600 hover:text-red-800 ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </Popconfirm>
                  </TableItem>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={tableHeaders.length} className="text-center py-4">
                  {t('Geen hardware gevonden.')}
                </td>
              </tr>
            )}
          </Table>
        </div>
      )}
    </div>
  ) : (
    <p>This page requires the hardware module</p>
  );
};

export default HardwareOverview;
